import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SectionHeader from './SectionHeader';
import SafeIcon from '../common/SafeIcon';
import { coffees } from '../data';

const questions = [
  {
    id: 1,
    title: "How do you usually brew your coffee at home?",
    options: [
      { id: 'drip', name: 'Auto Drip', icon: 'Coffee' },
      { id: 'pour', name: 'Pour Over', icon: 'Filter' },
      { id: 'press', name: 'French Press', icon: 'Box' },
      { id: 'espresso', name: 'Espresso', icon: 'Zap' }
    ]
  },
  {
    id: 2,
    title: "What roast level do you typically prefer?",
    options: [
      { id: 'light', name: 'Light & Bright', icon: 'Sun' },
      { id: 'medium', name: 'Smooth Medium', icon: 'Sunrise' },
      { id: 'dark', name: 'Bold & Dark', icon: 'Moon' },
      { id: 'surprise', name: 'Surprise Me', icon: 'HelpCircle' }
    ]
  },
  {
    id: 3,
    title: "Which flavor notes excite you the most?",
    options: [
      { id: 'fruity', name: 'Fruity & Floral', icon: 'Feather' },
      { id: 'chocolate', name: 'Chocolate & Nutty', icon: 'Hexagon' },
      { id: 'sweet', name: 'Caramel & Sweet', icon: 'Heart' },
      { id: 'smoky', name: 'Earthy & Smoky', icon: 'Cloud' }
    ]
  }
];

const RoastFinder = ({ onAddToCart }) => {
  const [step, setStep] = useState(0); // 0-2: questions, 3: loading, 4: result
  const [answers, setAnswers] = useState({});

  const handleOptionSelect = (qId, optId) => {
    setAnswers(prev => ({ ...prev, [qId]: optId }));
    if (step < 2) {
      setStep(step + 1);
    } else {
      setStep(3); // Loading state
      setTimeout(() => {
        setStep(4); // Result state
      }, 1500);
    }
  };

  const resetQuiz = () => {
    setAnswers({});
    setStep(0);
  };

  // Fake recommendation logic based on data
  const recommendedCoffee = coffees[1]; // Guji Highland

  return (
    <section className="py-24 bg-cream bg-notebook-lines relative overflow-hidden" id="quiz">
      <div className="max-w-4xl mx-auto px-6">
        <SectionHeader 
          title="Find Your Perfect Cup" 
          subtitle="Let's narrow down the Atlas for you."
          icon="Compass"
        />
        
        <div className="bg-white p-8 md:p-12 shadow-xl border border-gray-100 rounded-sm relative min-h-[400px] flex flex-col">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-24 h-6 bg-white/50 border border-gray-200 shadow-sm transform -rotate-2 backdrop-blur-sm"></div>
          
          <AnimatePresence mode="wait">
            {step < 3 ? (
              <motion.div 
                key={`q-${step}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex-1 flex flex-col"
              >
                <div className="flex justify-between items-center mb-8 text-sm font-medium text-gray-400 uppercase tracking-widest">
                  <span>Question {step + 1} of 3</span>
                  <div className="flex gap-1">
                    {[0, 1, 2].map(i => (
                      <div key={i} className={`h-1 w-8 rounded-full transition-colors duration-300 ${i <= step ? 'bg-moss' : 'bg-gray-200'}`}></div>
                    ))}
                  </div>
                </div>
                
                <h3 className="font-serif text-3xl font-bold text-center mb-10 text-espresso" aria-live="polite">
                  {questions[step].title}
                </h3>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-auto">
                  {questions[step].options.map(method => (
                    <button 
                      key={method.id}
                      onClick={() => handleOptionSelect(questions[step].id, method.id)}
                      aria-label={`Select ${method.name}`}
                      className="flex flex-col items-center justify-center p-6 border-2 border-gray-100 hover:border-moss hover:bg-moss/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-moss rounded-lg transition-all gap-4 group cursor-pointer"
                    >
                      <SafeIcon name={method.icon} className="text-4xl text-gray-400 group-hover:text-moss transition-colors" />
                      <span className="font-medium text-espresso text-sm text-center">{method.name}</span>
                    </button>
                  ))}
                </div>
                
                {step > 0 && (
                  <div className="mt-8 text-center">
                    <button 
                      onClick={() => setStep(step - 1)} 
                      className="text-gray-500 hover:text-espresso text-sm underline cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-moss rounded-sm p-1"
                    >
                      Go back
                    </button>
                  </div>
                )}
              </motion.div>
            ) : step === 3 ? (
              <motion.div 
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex-1 flex flex-col items-center justify-center text-espresso"
              >
                <SafeIcon name="Loader" className="animate-spin text-4xl text-moss mb-6" />
                <h3 className="font-serif text-2xl font-bold" aria-live="polite">Analyzing your preferences...</h3>
                <p className="text-gray-500 mt-2 font-handwriting text-xl">Consulting the roaster's log</p>
              </motion.div>
            ) : (
              <motion.div 
                key="result"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex-1 flex flex-col items-center text-center"
              >
                <div className="inline-flex items-center gap-2 bg-moss/10 text-moss px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6">
                  <SafeIcon name="CheckCircle" /> Perfect Match
                </div>
                <h3 className="font-serif text-4xl font-bold text-espresso mb-8">We found your brew.</h3>
                
                <div className="bg-cream-paper border border-gray-200 p-6 flex flex-col md:flex-row gap-6 items-center text-left max-w-2xl w-full mx-auto shadow-sm">
                  <img src={recommendedCoffee.image} alt={recommendedCoffee.name} className="w-32 h-32 object-cover rounded-sm shadow-md" />
                  <div className="flex-1">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-moss mb-1">{recommendedCoffee.origin}</p>
                    <h4 className="font-serif text-2xl font-bold text-espresso mb-2">{recommendedCoffee.name}</h4>
                    <p className="text-sm text-gray-600 mb-4">Based on your answers, you'll love the {recommendedCoffee.notes.join(', ').toLowerCase()} notes in this {recommendedCoffee.roast.toLowerCase()} roast.</p>
                    <div className="flex gap-4 items-center">
                      <button 
                        onClick={() => onAddToCart(recommendedCoffee)}
                        className="bg-moss hover:bg-moss-light text-cream px-6 py-3 font-bold uppercase tracking-widest text-[10px] transition-colors shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-moss offset-2"
                      >
                        Add to Cart — ${recommendedCoffee.price.toFixed(2)}
                      </button>
                    </div>
                  </div>
                </div>
                
                <button 
                  onClick={resetQuiz} 
                  className="mt-8 text-gray-500 hover:text-espresso text-sm underline cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-moss rounded-sm p-1"
                >
                  Retake Quiz
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default RoastFinder;